import { useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
	Loader2,
	StickyNote,
	Mail,
	Phone,
	MessageCircle,
	Linkedin,
	CheckSquare,
	CalendarDays,
	UserCheck,
	Trash2,
	Pen,
	Building2,
	Briefcase
} from 'lucide-react';
import {
	Badge,
	useToast
} from '@hiring-intelligence/ui';
import {
	CONTACT_STATUS_OPTIONS,
	SENTIMENT_OPTIONS,
	CRM_MAIN_INDUSTRIES,
	CRM_INDUSTRY_GROUPS,
	DEAL_STAGE_OPTIONS
} from '@hiring-intelligence/shared';
import { format } from 'date-fns';
import { crmContactsApi } from '@/services/api';
import { useAuthStore } from '@/stores/auth';
import { ActivityLog, type ActivityLogHandle } from '@/components/crm/ActivityLog';
import {
	CrmDetailLayout,
	IdentityHeader,
	QuickActionsBar,
	defaultMoreMenu,
	AboutCard,
	EditableTextField,
	EditableTextarea,
	EditableSelectField,
	ReadOnlyField,
	DataHighlights,
	AssociationCard,
	AssociationItem
} from '@/components/crm/layout';

const STATUS_BADGE: Record<string, 'default' | 'secondary' | 'success' | 'destructive' | 'outline'> = {
	client: 'success', candidate: 'default', lead: 'secondary', partner: 'outline', other: 'secondary'
};
const SENTIMENT_DOT: Record<string, string> = {
	cold: 'bg-blue-500', warm: 'bg-amber-500', hot: 'bg-red-500'
};

const NOT_SET = '__none__';
function asSelectOptions<T extends { value: string; label: string }>(opts: readonly T[], notSetLabel = 'Not set') {
	return [{ value: NOT_SET, label: notSetLabel }, ...opts];
}

function initials(name?: string | null) {
	if (!name) return '?';
	return name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('');
}

function formatDate(d?: string | Date | null) {
	if (!d) return null;
	try { return format(new Date(d), "d MMM yyyy, HH:mm"); } catch { return null; }
}

export default function ContactDetailPage() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { toast } = useToast();
	const currentUser = useAuthStore((s) => s.user);
	const activityRef = useRef<HTMLDivElement>(null);
	const activityLogRef = useRef<ActivityLogHandle>(null);

	const { data, isLoading } = useQuery({
		queryKey: ['crm-contact', id],
		queryFn: () => crmContactsApi.get(id!),
		enabled: !!id
	});
	const contact: any = data?.data;

	const updateMutation = useMutation({
		mutationFn: (patch: Record<string, any>) => crmContactsApi.update(id!, patch),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['crm-contact', id] });
			queryClient.invalidateQueries({ queryKey: ['crm-contacts'] });
			toast({ title: 'Saved' });
		},
		onError: (error: Error) => toast({ title: 'Error', description: error.message, variant: 'destructive' })
	});

	const deleteMutation = useMutation({
		mutationFn: () => crmContactsApi.remove(id!),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['crm-contacts'] });
			toast({ title: 'Contact deleted' });
			navigate('/contacts');
		},
		onError: (error: Error) => toast({ title: 'Error', description: error.message, variant: 'destructive' })
	});

	const save = (key: string) => (raw: string) => {
		const value = raw === NOT_SET ? null : raw.trim() === '' ? null : raw;
		updateMutation.mutate({ [key]: value });
	};
	const saveIndustryPair = (main: string) => {
		const next = main === NOT_SET ? null : main;
		updateMutation.mutate({ mainIndustry: next, subIndustry: null });
	};
	const subIndustryOptions = useMemo(() => {
		if (!contact?.mainIndustry) return [];
		return (CRM_INDUSTRY_GROUPS[contact.mainIndustry] || []).map((s) => ({ value: s, label: s }));
	}, [contact?.mainIndustry]);

	const scrollToActivity = () => {
		activityRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
	};
	// Quick-action bar → preselect the matching activity type in the composer, then scroll to it.
	const startActivity = (type: string) => {
		activityLogRef.current?.startActivity(type);
		scrollToActivity();
	};

	if (isLoading) {
		return <div className="flex items-center justify-center p-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
	}
	if (!contact) {
		return (
			<div className="p-6">
				<Link to="/contacts" className="text-sm text-primary hover:underline">← Contacts</Link>
				<p className="mt-6 text-center text-muted-foreground">Contact not found.</p>
			</div>
		);
	}

	const statusLabel = CONTACT_STATUS_OPTIONS.find((o) => o.value === contact.status)?.label;
	const sentimentLabel = SENTIMENT_OPTIONS.find((o) => o.value === contact.sentiment)?.label;

	const left = (
		<>
			<IdentityHeader
				backTo="/contacts"
				backLabel="Contacts"
				avatar={
					<div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary font-semibold text-base">
						{initials(contact.name)}
					</div>
				}
				title={contact.name}
				subtitle={
					<span>
						{contact.title || <span className="italic">Add title</span>}
						{contact.company?.name && (
							<>
								{' '}at{' '}
								<Link to={`/companies/${contact.companyId}`} className="text-primary hover:underline">
									{contact.company.name}
								</Link>
							</>
						)}
					</span>
				}
				metaRow={
					contact.email && (
						<a href={`mailto:${contact.email}`} className="hover:underline">{contact.email}</a>
					)
				}
				titleBadges={
					<>
						{statusLabel && <Badge variant={STATUS_BADGE[contact.status] || 'secondary'}>{statusLabel}</Badge>}
						{contact.sentiment && (
							<span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
								<span className={`h-2 w-2 rounded-full ${SENTIMENT_DOT[contact.sentiment] || 'bg-muted'}`} />
								{sentimentLabel}
							</span>
						)}
						{contact.candidateVaultId && (
							<Badge variant="outline" className="gap-1"><UserCheck className="h-3 w-3" /> Talent vault</Badge>
						)}
					</>
				}
				actions={[
					{ label: 'Edit details', icon: <Pen className="h-4 w-4" />, onClick: scrollToActivity },
					...(currentUser?.role === 'super_admin' ? [{
						label: 'Delete contact',
						icon: <Trash2 className="h-4 w-4" />,
						onClick: () => {
							if (window.confirm('Delete this contact? This cannot be undone.')) deleteMutation.mutate();
						},
						destructive: true
					}] : [])
				]}
			/>

			<QuickActionsBar
				actions={[
					{ label: 'Note', icon: StickyNote, onClick: () => startActivity('note') },
					{ label: 'Email', icon: Mail, onClick: () => startActivity('email') },
					{ label: 'Call', icon: Phone, onClick: () => startActivity('call') },
					{ label: 'Task', icon: CheckSquare, onClick: () => startActivity('task') },
					{ label: 'Meeting', icon: CalendarDays, onClick: () => startActivity('meeting') }
				]}
				moreMenu={defaultMoreMenu({
					onLogSMS: () => startActivity('sms'),
					onLogLinkedIn: () => startActivity('linkedin'),
					onLogWhatsApp: () => startActivity('whatsapp')
				})}
			/>

			<AboutCard title={contact.status === 'candidate' ? 'Talent Card' : 'Client Card'}>
				<EditableSelectField
					label="Current Status"
					value={contact.status}
					options={asSelectOptions(CONTACT_STATUS_OPTIONS, 'No status')}
					onSave={save('status')}
					renderDisplay={(v) => v
						? <Badge variant={STATUS_BADGE[v] || 'secondary'}>{CONTACT_STATUS_OPTIONS.find((o) => o.value === v)?.label || v}</Badge>
						: <span className="text-sm text-muted-foreground italic">No status</span>
					}
				/>
				<ReadOnlyField label="Contact Owner">
					{contact.owner?.name || <span className="text-muted-foreground italic">No owner</span>}
				</ReadOnlyField>
				<EditableTextField label="Email" type="email" value={contact.email} onSave={save('email')} placeholder="email@example.com" />
				<EditableTextField label="Phone Number" type="tel" value={contact.phone} onSave={save('phone')} placeholder="+62 ..." />
				<EditableTextField label="WhatsApp Phone Number" type="tel" value={contact.whatsapp} onSave={save('whatsapp')} placeholder="+62 ..." />
				<EditableSelectField
					label="Sentiment"
					value={contact.sentiment}
					options={asSelectOptions(SENTIMENT_OPTIONS)}
					onSave={save('sentiment')}
				/>
				<EditableSelectField
					label="Main Industry"
					value={contact.mainIndustry}
					options={asSelectOptions(CRM_MAIN_INDUSTRIES.map((m) => ({ value: m, label: m })))}
					onSave={saveIndustryPair}
				/>
				<EditableSelectField
					label="Sub-Industry"
					value={contact.subIndustry}
					options={asSelectOptions(subIndustryOptions)}
					onSave={save('subIndustry')}
					disabled={!contact.mainIndustry}
					placeholder={contact.mainIndustry ? 'Select sub-industry' : 'Set main industry first'}
				/>
				<ReadOnlyField label="Company">
					{contact.company?.name
						? <Link to={`/companies/${contact.companyId}`} className="text-primary hover:underline">{contact.company.name}</Link>
						: <span className="text-muted-foreground italic">No company</span>}
				</ReadOnlyField>
				<EditableTextField label="Job Title" value={contact.title} onSave={save('title')} placeholder="e.g. Head of Talent" />
				<EditableTextField label="Country / Region" value={contact.country} onSave={save('country')} placeholder="e.g. Indonesia" />
				<EditableTextField label="Preferred Language" value={contact.preferredLanguage} onSave={save('preferredLanguage')} placeholder="e.g. English" />
				<EditableTextField label="LinkedIn Account" type="url" value={contact.linkedinUrl} onSave={save('linkedinUrl')} placeholder="https://linkedin.com/in/..." />
				<EditableTextField label="Source" value={contact.source} onSave={save('source')} placeholder="e.g. Referral" />
				<EditableTextarea label="Notes" value={contact.notes} onSave={save('notes')} placeholder="Anything noteworthy about this contact..." />
			</AboutCard>
		</>
	);

	const deals = contact.deals || [];
	const company = contact.company;
	const linkedHiringRequests: any[] = deals.flatMap((d: any) => (d.hiringRequests || []).map((hr: any) => ({ ...hr, viaDeal: d.title })));
	const fmtHrStatus = (s?: string) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

	const right = (
		<>
			<AssociationCard title="Deals" count={deals.length} emptyMessage="No deals linked yet.">
				{deals.length > 0 && deals.map((deal: any) => (
					<AssociationItem
						key={deal.id}
						href={`/deals/${deal.id}`}
						title={deal.title}
						meta={
							<>
								{deal.roleTitle && <div className="truncate">{deal.roleTitle}</div>}
								{deal.company?.name && <div className="truncate">{deal.company.name}</div>}
							</>
						}
						badge={<Badge variant="secondary" className="text-[10px]">{DEAL_STAGE_OPTIONS.find((o) => o.value === deal.stage)?.label || deal.stage}</Badge>}
					/>
				))}
			</AssociationCard>

			<AssociationCard title="Companies" count={company ? 1 : 0} icon={<Building2 className="h-3.5 w-3.5" />} emptyMessage="No company associated.">
				{company && (
					<AssociationItem
						href={`/companies/${contact.companyId}`}
						title={
							<span className="inline-flex items-center gap-1.5">
								{company.name}
								<Badge variant="secondary" className="text-[10px]">Primary</Badge>
							</span>
						}
						meta={
							<>
								{company.website && <div className="truncate">{company.website}</div>}
								{company.industry && <div className="truncate">{company.industry}</div>}
							</>
						}
					/>
				)}
			</AssociationCard>

			<AssociationCard title="Attachments" icon={<Briefcase className="h-3.5 w-3.5" />} emptyMessage="Files attached to this contact will appear here." />
		</>
	);

	const overviewBody = (
		<>
			<DataHighlights
				items={[
					{ label: 'Create Date', value: formatDate(contact.createdAt) },
					{ label: 'Lifecycle Stage', value: statusLabel || '—' },
					{ label: 'Last Activity Date', value: formatDate(contact.lastActivityAt) || '—' }
				]}
			/>
			<div className="rounded-lg border bg-card">
				<div className="border-b px-4 py-2.5 text-sm font-semibold">
					Hiring requests <span className="text-muted-foreground">({linkedHiringRequests.length})</span>
				</div>
				{linkedHiringRequests.length === 0 ? (
					<p className="text-sm text-muted-foreground text-center py-8">No hiring requests linked through deals yet.</p>
				) : (
					<div className="divide-y">
						{linkedHiringRequests.map((hr: any) => (
							<div key={hr.id} className="flex items-center justify-between gap-2 px-4 py-2.5">
								<div className="min-w-0">
									<Link to={`/hiring-requests/${hr.id}`} className="text-sm font-medium text-primary hover:underline truncate block">{hr.roleTitle}</Link>
									<div className="text-xs text-muted-foreground truncate">via {hr.viaDeal}{hr.dealCode ? ` · ${hr.dealCode}` : ''}</div>
								</div>
								<Badge variant="secondary" className="text-[10px] shrink-0">{fmtHrStatus(hr.status)}</Badge>
							</div>
						))}
					</div>
				)}
			</div>
			<div ref={activityRef}>
				<div className="rounded-lg border bg-card">
					<div className="border-b px-4 py-2.5 text-sm font-semibold">Recent activities</div>
					<div className="px-4 py-3">
						<ActivityLog ref={activityLogRef} contactId={contact.id} />
					</div>
				</div>
			</div>
		</>
	);

	return (
		<CrmDetailLayout
			left={left}
			center={overviewBody}
			right={right}
		/>
	);
}
